import {useData} from "vike-react/useData";
import {ApiLivestreamData, ApiStreamerSocial} from "#/src/definitions/ApiDefinitions";
import {StreamerFaqs} from "#/src/pages/live/@id/StreamerFaqs";
import {StreamerDescriptions} from "#/src/pages/live/@id/StreamerDescriptions";
import {StreamerAvatar} from "#/src/components/streamer/StreamerAvatar";
import {getStreamerImageBySocialType} from "#/src/utils/social-utils";
import {Sort} from "#/src/utils/sort-utils";
import {StreamerGameIcon} from "#/src/components/streamer/StreamerGameIcon";
import {YoutubeVideos} from "#/src/pages/live/@id/YoutubeVideos";
import {AppPageContext, DataWrapped} from "#/src/definitions/AppDefinitions";
import AppConstants from "#/src/utils/app-constants";
import {composeHeaders} from "#/src/utils/api-request-utils";
import {usePageContext} from "vike-react/usePageContext";
import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {LoadableButton} from "#/src/components/LoadableButton";
import {StreamerGameName} from "#/src/components/streamer/StreamerGameName";
import {FacebookEmbed} from "#/src/pages/live/@id/FacebookEmbed";
import {AdSection} from "#/src/pages/live/@id/AdSection";
import styled from "@emotion/styled";
import {truncateString} from "#/src/utils/string-utils";
import {navigate} from "vike/client/router";

export const ButtonContainer = styled.div`
  button:first-of-type {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  button:not(:first-of-type):not(:last-of-type) {
    border-radius: 0;
  }

  button:last-of-type {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const Ellipsis = styled.span`
    @media (max-width: 800px) {
        display: block;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        width: calc(75vw);
    }
`;

export function Content() {
  const pageContext = usePageContext() as AppPageContext;
  const {data: livestreamData} = useData() as DataWrapped<ApiLivestreamData>;
  const [followed, setFollowed] = useState(livestreamData.streamer.followed);
  useEffect(() => {
    setFollowed(livestreamData.streamer.followed);
  }, [livestreamData]);

  async function onFollowToggle() {
    if(!pageContext.user.loggedIn) {
      await navigate("/login");
      return;
    }
    const response = await fetch(`${AppConstants.API_ROUTE}/livestream/${livestreamData.streamer.slug}/followers`, {
      method: followed ? "DELETE" : "POST",
      headers: composeHeaders(pageContext)
    });
    if (response.ok) {
      const isFollowed = !followed;
      if (isFollowed) {
        toast(`Od teraz obserwujesz streamera ${livestreamData.streamer.username}!`);
      } else {
        toast(`Przestałeś obserwować streamera ${livestreamData.streamer.username}!`);
      }
      setFollowed(isFollowed);
    } else {
      const errorResponse = await response.json() as any;
      if (errorResponse && errorResponse.errors) {
        errorResponse.errors.forEach(err => toast(err));
      }
    }
    return Promise.resolve();
  }

  function onEmbedRefresh() {
    let liveEmbed = document.getElementById('liveEmbed') as HTMLIFrameElement;
    // noinspection SillyAssignmentJS
    liveEmbed.src = liveEmbed.src;
    let chatEmbed = document.getElementById('chatEmbed') as HTMLIFrameElement;
    // noinspection SillyAssignmentJS
    chatEmbed.src = chatEmbed.src;
  }

  function onFullscreen() {
    const liveView = document.getElementById("liveView");
    const requestMethod = liveView['requestFullscreen'] || liveView['webkitRequestFullscreen']
      || liveView['mozRequestFullscreen'] || liveView['msRequestFullscreen'];
    if (requestMethod) {
      requestMethod.call(liveView);
    }
  }

  function render() {
    return <main>
      <section className="twitch-panel">
        <div className="container-xxl">
          <div className="row">
            <div className="col-xl-12">
              <div className="twitch-panel-user">
                <div>
                  <div className="stream-user">
                    <StreamerAvatar streamer={livestreamData.streamer}/>
                    <div className="stream-content">{renderLiveContent()}</div>
                  </div>
                  <div className={"stream-socials"}>{renderSocials()}</div>
                </div>
                <div className="flex-group">
                  <a href={livestreamData.streamer.subscriptionLink} target={"_blank"} rel={"noreferrer noopener"} role="button" className="btn btn-sub">
                    Subskrybuj
                  </a>
                  {livestreamData.streamer.donationLink && <a href={livestreamData.streamer.donationLink} target={"_blank"} rel={"noreferrer noopener"} role="button" className="btn btn-primary">
                    Dotuj
                  </a>}
                  {renderFollowButton()}
                  <ButtonContainer className={"d-lg-block d-none"}>
                    <button className={"btn btn-secondary btn-livestream-action"}
                            onClick={() => window.open(livestreamData.streamer.liveEmbed, '', 'width=1200, height=600')}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6"/>
                        <path d="M11 13l9 -9"/>
                        <path d="M15 4h5v5"/>
                      </svg>
                    </button>
                    <button className={"btn btn-secondary btn-livestream-action"} onClick={onEmbedRefresh}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/>
                        <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/>
                      </svg>
                    </button>
                    <button className={"btn btn-secondary btn-livestream-action my-auto"} onClick={onFullscreen}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 4l4 0l0 4"/>
                        <path d="M14 10l6 -6"/>
                        <path d="M8 20l-4 0l0 -4"/>
                        <path d="M4 20l6 -6"/>
                        <path d="M16 20l4 0l0 -4"/>
                        <path d="M14 14l6 6"/>
                        <path d="M8 4l-4 0l0 4"/>
                        <path d="M4 4l6 6"/>
                      </svg>
                    </button>
                  </ButtonContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="menu-live">
        <div className="container-xxl">
          <div className="row">
            <div className="col-xl-12">
              <ul className="nav-stream">
                <li className="nav-stream-item">
                  <a href="#live" className="nav-stream-link active">Live</a>
                </li>
                <li className="nav-stream-item">
                  <a href="#channel" className="nav-stream-link">
                    Opis kanału
                  </a>
                </li>
                {livestreamData.streamer.facebookEmbed && <li className="nav-stream-item">
                  <a href="#post" className="nav-stream-link">Ostatnie posty</a>
                </li>}
                {livestreamData.streamer.faq.length > 0 && <li className="nav-stream-item">
                  <a href="#faq" className="nav-stream-link">FAQ</a>
                </li>}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <AdSection/>
      <YoutubeVideos/>
      <StreamerDescriptions/>
      <FacebookEmbed/>
      <StreamerFaqs/>
    </main>
  }

  function renderLiveContent() {
    if (!livestreamData.streamer.isLive) {
      return <>
        <div className="stream-name">
          <a href="#" className="stream-link">{livestreamData.streamer.username}</a>
          <span className={"badge badge-offline"}>Offline</span>
        </div>
      </>
    }
    return <>
      <div className="stream-name">
        <a href="#" className="stream-link">{livestreamData.streamer.username}</a>
        <span className={"badge badge-online"}>{livestreamData.streamer.viewers}</span>
      </div>
      <div className="stream-game">
        <StreamerGameIcon game={livestreamData.streamer.game}/>
        <StreamerGameName streamer={livestreamData.streamer}/>
      </div>
      <div className="stream-title">
        <Ellipsis>{truncateString(livestreamData.streamer.liveTitle, 100)}</Ellipsis>
      </div>
    </>
  }

  function renderSocials() {
    if (livestreamData.streamer.socials.length === 0) {
      return <></>
    }
    const sorted = Sort.byDisplayPriority(livestreamData.streamer.socials);
    return <div className={"col-xl-12 mb-2 d-flex"}>
      <span className={"streamer-social-text"} style={{fontSize: ".75rem", marginTop: "auto", marginBottom: "auto", marginRight: "1em"}}>Moje socjale:</span>
      {sorted.map((social: ApiStreamerSocial) => {
        return <a key={social.id} href={social.url} target={"_blank"} rel={"noreferrer noopener"} className={"me-3 streamer-social-icon"}>
          {getStreamerImageBySocialType(social.type)}
        </a>
      })}
    </div>
  }

  function renderFollowButton() {
    if (!followed) {
      return <LoadableButton className="btn btn-primary" onClick={onFollowToggle}>Follow</LoadableButton>
    }
    return <LoadableButton className="btn btn-secondary" onClick={onFollowToggle}>Unfollow</LoadableButton>
  }

  return render();
}